import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  ArrowBack,
  ArrowForward,
  Campaign,
  CheckCircle,
  Description,
  Groups,
  Send,
} from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import RdCampaignStep1 from "./RdCampaignStep1";
import RdCampaignStep2 from "./RdCampaignStep2";
import RdCampaignStep3 from "./RdCampaignStep3";
import QualityRatingsMessage from "./RdQualtityRatingsMessage";
import LoadingWidget from "../../widgets/LoadingWidget";
import useApiEp from "../../hooks/useApiEp";
import { API_ENDPOINT } from "../../utils/constants";
import {
  getErrorResponse,
  getHeader,
  toastError,
} from "../../utils/util";
import { useDialog } from "../../context/DialogContext";

const STEPS = [
  {
    label: "Campaign setup",
    description: "Name, template and delivery settings",
    icon: Description,
  },
  {
    label: "Target audience",
    description: "Choose who should receive the campaign",
    icon: Groups,
  },
  {
    label: "Review & send",
    description: "Confirm everything before broadcast",
    icon: CheckCircle,
  },
];

const CampaignStepIcon = ({ active, completed, icon }) => {
  const step = STEPS[Number(icon) - 1] || STEPS[0];
  const Icon = completed ? CheckCircle : step.icon;
  const highlighted = active || completed;

  return (
    <Box
      sx={{
        width: 40,
        height: 40,
        display: "grid",
        placeItems: "center",
        borderRadius: "50%",
        bgcolor: highlighted ? "primary.main" : "grey.100",
        color: highlighted ? "primary.contrastText" : "text.secondary",
        border: "1px solid",
        borderColor: highlighted ? "primary.main" : "divider",
        boxShadow: active ? "0 0 0 5px rgba(18, 140, 126, 0.10)" : "none",
        transition: "all 180ms ease",
      }}
    >
      <Icon sx={{ fontSize: 20 }} />
    </Box>
  );
};

const RdCampaign = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showDialog } = useDialog();
  const contentRef = useRef(null);

  const [activeStep, setActiveStep] = useState(0);
  const [scheduledDate, setScheduledDate] = useState();
  const [contactGroup, setContactGroup] = useState(0);
  const [contactGroupName, setContactGroupName] = useState("");
  const [campaignGroupName, setCampaignGroupName] = useState("");
  const [contactsType, setContactsType] = useState("contacts");
  const [checkingStep, setCheckingStep] = useState(false);
  const [sendingCampaign, setSendingCampaign] = useState(false);
  const [campaignPost, setCampaignPost] = useState({
    campaignName: "",
    header: {},
    templateId: "",
    bodyParams: [],
    template: null,
    contacts: [],
    cards: [],
    splitAcrossAccounts: false,
  });

  const phoneResponse = useApiEp("/user/phone-details");
  const templateResponse = useApiEp("/api/get-templates-sync");
  const rawQualityRating =
    phoneResponse.data?.data?.quality_rating ||
    phoneResponse.data?.data?.quality_score?.score;
  const qualityRating = rawQualityRating
    ? String(rawQualityRating).toUpperCase()
    : null;
  const qualityBypassEnabled =
    phoneResponse.data?.bypassNumberQuality === true ||
    phoneResponse.data?.data?.bypassNumberQuality === true;

  useEffect(() => {
    contentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [activeStep]);

  useEffect(() => {
    const requestedTab = searchParams.get("gn");
    const requestedName = searchParams.get("name") || "";
    const requestedCampaignGroup = searchParams.get("groupName") || "";
    const requestedType =
      searchParams.get("contactsType") === "database"
        ? "database"
        : "contacts";

    let audienceTab = ["1", "2"].includes(requestedTab)
      ? Number(requestedTab)
      : 0;

    if (requestedName) {
      audienceTab = requestedType === "database" ? 2 : 1;
    }

    setContactGroup(audienceTab);
    setContactGroupName(requestedName);
    setCampaignGroupName(requestedCampaignGroup);
    setContactsType(requestedType);
  }, [searchParams]);

  useEffect(() => {
    if (scheduledDate) {
      setCampaignPost((previous) => ({
        ...previous,
        scheduledDate,
      }));
    }
  }, [scheduledDate]);

  const doCampaign = async (autoSplit = false) => {
    const contacts = Array.isArray(campaignPost.contacts)
      ? campaignPost.contacts
      : [];

    if (!campaignPost.campaignName?.trim()) {
      toast.error("Please enter a campaign name");
      return;
    }
    if (contacts.length < 1) {
      toast.error("Please select contacts for the campaign");
      return;
    }
    if (!campaignPost.template) {
      toast.error("Please select a template for the campaign");
      return;
    }

    const isStatic =
      campaignPost.isStaticParams && campaignPost.bodyParams.length > 0;
    if (isStatic && campaignPost.bodyParams.some((item) => item === "")) {
      toast.error("Please enter all static parameter values");
      return;
    }
    if (campaignPost.isCampaignScheduled && !scheduledDate) {
      toast.error("Please select a campaign schedule");
      return;
    }
    if (campaignPost.smartRetry && !campaignPost.retryIntervals) {
      toast.error("Please specify retry intervals");
      return;
    }
    if (campaignPost.isMarketing && !campaignPost.contactGroup?._id) {
      toast.error("Please select a valid database audience");
      return;
    }

    const payload = {
      campaignName: campaignPost.campaignName.trim(),
      templateId: campaignPost.template.id,
      header: campaignPost.header,
      ...(autoSplit ? { autoSplit: true } : {}),
      ...(campaignPost.useDefaultMedia ? { useDefaultMedia: true } : {}),
    };

    if (campaignPost.isCarousel) payload.cards = campaignPost.cards;
    if (campaignPost.contactMapping) {
      payload.contactMapping = campaignPost.contactMapping;
    }
    if (campaignPost.headerMapping) {
      payload.headerMapping = campaignPost.headerMapping;
    }

    if (campaignPost.isMarketing) {
      payload.isMarketing = true;
      payload.categoryId = campaignPost.contactGroup._id;
      payload.indexStart = {
        start: campaignPost.startIndex,
        end: campaignPost.endIndex,
      };
    } else {
      payload.contacts = contacts;
    }

    if (isStatic) payload.bodyParams = campaignPost.bodyParams;
    if (campaignPost.isCampaignScheduled) {
      payload.isCampaignScheduled = true;
      payload.scheduledDate = scheduledDate;
    }
    if (campaignPost.marketingQuery) {
      payload.marketingQuery = campaignPost.marketingQuery;
    }
    if (campaignPost.smartRetry) {
      payload.smartRetry = campaignPost.smartRetry;
      payload.retryIntervals = campaignPost.retryIntervals;
    }
    if (campaignPost.splitAccounts?.length) {
      payload.splitAccounts = campaignPost.splitAccounts;
    }
    if (campaignPost.urlParams) payload.urlParams = campaignPost.urlParams;
    if (campaignPost.catalogueThumbnail) {
      payload.catalogueThumbnail = campaignPost.catalogueThumbnail;
    }
    if (!campaignPost.isMarketing && campaignPost.contactGroup) {
      payload.contactGroupSelected = true;
      payload.contactGroup = campaignPost.contactGroup;
    }
    if (campaignGroupName) payload.campaignGroupName = campaignGroupName;
    if (campaignPost.isMainAccountExcluded) {
      payload.isMainAccountExcluded = true;
    }

    try {
      setSendingCampaign(true);
      const response = await axios.post(
        `${API_ENDPOINT}/campaign/create-campaign`,
        payload,
        getHeader()
      );
      toast.success(response.data.message);
      navigate("/home/queue-monitor");
    } catch (error) {
      showDialog("Unable to send campaign", getErrorResponse(error), {
        type: "error",
        onConfirm: () => {},
      });
    } finally {
      setSendingCampaign(false);
    }
  };

  const validateCampaignSetup = async () => {
    if (!campaignPost.campaignName?.trim()) {
      toast.error("Please enter a campaign name");
      return false;
    }
    if (!campaignPost.template) {
      toast.error("Please select a template");
      return false;
    }
    if (
      campaignPost.isCarousel &&
      !campaignPost.useDefaultMedia &&
      campaignPost.cards?.length !== campaignPost.carouselMediaCount
    ) {
      toast.error("Select one media item for every carousel card");
      return false;
    }
    if (
      campaignPost.splitAcrossAccounts &&
      !(campaignPost.splitAccounts?.length > 0)
    ) {
      toast.error("Select at least one account for account splitting");
      return false;
    }

    const splitCount = campaignPost.splitAccounts?.length || 0;
    const approved = campaignPost.template.status?.toUpperCase() === "APPROVED";
    const canUseMappedAccounts =
      splitCount > 0 && campaignPost.isMainAccountExcluded;

    if (!approved && !canUseMappedAccounts) {
      toast.error("Please select an approved template to continue");
      return false;
    }

    try {
      setCheckingStep(true);
      const response = await axios.post(
        `${API_ENDPOINT}/campaign/check-campaign-name`,
        { campaignName: campaignPost.campaignName.trim() },
        getHeader()
      );

      if (!response.data.exists) {
        if (response.data.message) toast.error(response.data.message);
        return false;
      }
      return true;
    } catch (error) {
      toastError(error);
      return false;
    } finally {
      setCheckingStep(false);
    }
  };

  const handlePrimaryAction = async () => {
    if (checkingStep || sendingCampaign) return;

    if (activeStep === 0) {
      if (await validateCampaignSetup()) setActiveStep(1);
      return;
    }

    if (activeStep === 1) {
      if (!Array.isArray(campaignPost.contacts) || campaignPost.contacts.length < 1) {
        toast.error("Please confirm a target audience before continuing");
        return;
      }
      setActiveStep(2);
      return;
    }

    const hasDeliveryPlan = campaignPost.dataSplitsList?.length > 0;
    showDialog(
      hasDeliveryPlan ? "Send this campaign with batching?" : "Send this campaign?",
      hasDeliveryPlan
        ? "Your campaign will use the reviewed AI delivery plan. Please confirm that you are ready to continue."
        : "Please confirm that the campaign details and target audience are correct.",
      {
        type: "warning",
        onConfirm: () => doCampaign(hasDeliveryPlan),
      }
    );
  };

  const handleBack = () => {
    if (activeStep > 0 && !sendingCampaign) {
      setActiveStep((previous) => previous - 1);
    }
  };

  const getStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <RdCampaignStep1
            qualityRating={qualityRating}
            templateD={templateResponse.data?.data || []}
            scheduledDate={scheduledDate}
            setScheduledDate={setScheduledDate}
            campaignPost={campaignPost}
            setCampaignPost={setCampaignPost}
          />
        );
      case 1:
        return (
          <RdCampaignStep2
            setActiveStep={setActiveStep}
            campaignPost={campaignPost}
            setCampaignPost={setCampaignPost}
            contactGroup={contactGroup}
            setContactGroup={setContactGroup}
            contactGroupName={contactGroupName}
            contactsType={contactsType}
          />
        );
      case 2:
        return (
          <RdCampaignStep3
            campaignPost={campaignPost}
            setCampaignPost={setCampaignPost}
          />
        );
      default:
        return null;
    }
  };

  if (templateResponse.isLoading) {
    return (
      <Box m={4}>
        <LoadingWidget
          label="Loading campaign templates..."
          type="circular"
          thickness={6}
          size={90}
        />
      </Box>
    );
  }

  if (templateResponse.isError) {
    return (
      <Box m={3}>
        <Alert severity="error">
          {getErrorResponse(templateResponse.error)}
        </Alert>
      </Box>
    );
  }

  const qualityBlocked =
    qualityRating &&
    !["GREEN", "UNKNOWN"].includes(qualityRating) &&
    !qualityBypassEnabled;

  if (qualityBlocked) {
    return (
      <Box m={3}>
        <QualityRatingsMessage qualityRating={qualityRating} />
      </Box>
    );
  }

  const primaryLabel =
    activeStep === 0
      ? "Continue to audience"
      : activeStep === 1
      ? "Review campaign"
      : "Send campaign";

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", py: 3 }}>
      <Container maxWidth="lg">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              mb: 3,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 46,
                  height: 46,
                  display: "grid",
                  placeItems: "center",
                  borderRadius: 2.25,
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  boxShadow: "0 8px 22px rgba(18, 140, 126, 0.18)",
                }}
              >
                <Campaign />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={800}>
                  Create WhatsApp campaign
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Build, target, review, and launch from one guided flow.
                </Typography>
              </Box>
            </Stack>

            {campaignGroupName && (
              <Chip
                label={`Campaign group: ${campaignGroupName}`}
                color="primary"
                variant="outlined"
              />
            )}
          </Box>

          {phoneResponse.isError && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              Phone-number quality information could not be loaded. You can
              continue configuring the campaign.
            </Alert>
          )}

          {qualityBypassEnabled &&
            qualityRating &&
            !["GREEN", "UNKNOWN"].includes(qualityRating) && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                Number-quality protection is bypassed for this account. You can
                continue, but monitor delivery and number health closely while
                the quality rating is {qualityRating}.
              </Alert>
            )}

          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{
              px: { xs: 0, sm: 2 },
              "& .MuiStepConnector-line": { borderColor: "divider" },
              "& .MuiStepLabel-label": { mt: 1 },
            }}
          >
            {STEPS.map((step, index) => (
              <Step key={step.label} completed={index < activeStep}>
                <StepLabel StepIconComponent={CampaignStepIcon}>
                  <Typography
                    variant="body2"
                    fontWeight={activeStep === index ? 700 : 600}
                    color={activeStep === index ? "text.primary" : "text.secondary"}
                  >
                    {step.label}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: { xs: "none", md: "block" }, mt: 0.25 }}
                  >
                    {step.description}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        <Paper
          ref={contentRef}
          elevation={0}
          sx={{
            mt: 2.5,
            p: { xs: 2, sm: 3 },
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            minHeight: 260,
          }}
        >
          {getStepContent()}
        </Paper>

        <Paper
          elevation={2}
          sx={{
            position: "sticky",
            bottom: 16,
            zIndex: 5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            mt: 2.5,
            p: 1.5,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2.5,
          }}
        >
          <Button
            variant="text"
            disabled={activeStep === 0 || sendingCampaign}
            onClick={handleBack}
            startIcon={<ArrowBack />}
            sx={{ minHeight: 44, px: { xs: 1.5, sm: 2.5 } }}
          >
            Back
          </Button>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: { xs: "none", sm: "block" } }}
            >
              Step {activeStep + 1} of {STEPS.length}
            </Typography>
            <LoadingButton
              variant="contained"
              loading={checkingStep || sendingCampaign}
              onClick={handlePrimaryAction}
              endIcon={activeStep === 2 ? <Send /> : <ArrowForward />}
              sx={{
                minHeight: 46,
                px: { xs: 2, sm: 3 },
                borderRadius: 2,
                fontWeight: 700,
              }}
            >
              {primaryLabel}
            </LoadingButton>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default RdCampaign;
